/* eslint-disable lingui/no-unlocalized-strings */
import * as Sentry from '@sentry/react';
import { useStytchB2BClient } from '@stytch/react/b2b';
import { useRef, useState } from 'react';
import useWebSocketModule, { ReadyState } from 'react-use-websocket';

const useWebSocket = (useWebSocketModule as unknown as { default: typeof useWebSocketModule })
  .default;

import { sessionDurationMinutes } from '@/constants/numbers';
import { useMe } from '@/providers/User';

import { reportMcpChartFailure } from './mcpChartObservability';
import {
  type ClientMessagePayload,
  type ClientMessageType,
  type WebSocketAIMessage,
  type WebSocketError,
  type WebSocketResponse,
  fetchAppBundleHtml,
  isUnknownWebSocketMessage,
  isWebSocketError,
  parseWebSocketResponse,
} from './message';

type AuthWebSocketParams = {
  onMessageReceived?: (message: WebSocketAIMessage) => void;
  onErrorReceived?: (message: WebSocketError) => void;
  onConnectionLost?: () => void;
};

const useAuthWebSocket = ({
  onMessageReceived,
  onErrorReceived,
  onConnectionLost,
}: AuthWebSocketParams) => {
  const currentUser = useMe();
  const { session } = useStytchB2BClient();
  const [jwtToken, setJwtToken] = useState(() => session.getTokens()?.session_jwt);
  const lastRefreshAttemptRef = useRef(0);
  const refreshInFlightRef = useRef(false);
  const stopSpanRef = useRef<() => void>(undefined);
  const messageQueueRef = useRef(Promise.resolve());
  const messageEpochRef = useRef(0);
  const activeSocketRef = useRef<EventTarget | null>(null);
  const resettingRef = useRef(false);
  const [isResetting, setIsResetting] = useState(false);
  const finishSpan = () => {
    const stopSpan = stopSpanRef.current;
    stopSpanRef.current = undefined;
    stopSpan?.();
  };
  const cancelRequest = () => {
    messageEpochRef.current += 1;
    messageQueueRef.current = Promise.resolve();
    finishSpan();
  };

  const refreshHandshakeToken = async () => {
    const currentToken = session.getTokens()?.session_jwt;
    if (currentToken && currentToken !== jwtToken) {
      setJwtToken(currentToken);
      return;
    }
    if (refreshInFlightRef.current || Date.now() - lastRefreshAttemptRef.current < 60_000) {
      return;
    }
    lastRefreshAttemptRef.current = Date.now();
    refreshInFlightRef.current = true;
    try {
      await session.authenticate({ session_duration_minutes: sessionDurationMinutes });
      setJwtToken(session.getTokens()?.session_jwt);
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      refreshInFlightRef.current = false;
    }
  };

  const websocketUrl = import.meta.env.VITE_MCP_SERVER_WS_URL;

  const socket = useWebSocket<WebSocketResponse>(websocketUrl, {
    protocols: jwtToken,
    onOpen: (event) => {
      activeSocketRef.current = event.target;
      resettingRef.current = false;
      setIsResetting(false);
      Sentry.addBreadcrumb({
        type: 'default',
        category: 'AI Websocket Opened',
        message: 'Socket opened',
        level: 'info',
      });
    },
    onClose: (event) => {
      if (event.target !== activeSocketRef.current) {
        return;
      }
      activeSocketRef.current = null;
      resettingRef.current = true;
      setIsResetting(true);
      setJwtToken(session.getTokens()?.session_jwt);
      cancelRequest();
      onConnectionLost?.();
      Sentry.addBreadcrumb({
        type: 'default',
        category: 'AI Websocket Closed',
        message: 'Socket closed',
        data: {
          code: event.code,
          reason: event.reason,
        },
        level: 'error',
      });
    },
    onError: async (event) => {
      if (event.target !== activeSocketRef.current) {
        if (!activeSocketRef.current) {
          await refreshHandshakeToken();
        }
        return;
      }
      console.error('Error connecting to websocket:', event);
      activeSocketRef.current = null;
      resettingRef.current = true;
      setIsResetting(true);
      setJwtToken(session.getTokens()?.session_jwt);
      cancelRequest();
      onConnectionLost?.();
    },
    retryOnError: true,
    reconnectAttempts: Infinity,
    reconnectInterval: (attempt) => Math.min(1000 * Math.pow(2, attempt), 30000),
    shouldReconnect: () => true,
    onMessage: (event: MessageEvent) => {
      if (resettingRef.current || event.target !== activeSocketRef.current) {
        return;
      }
      const epoch = messageEpochRef.current;
      const processMessage = async () => {
        if (epoch !== messageEpochRef.current) {
          return;
        }
        let parsedEventData: unknown;
        try {
          parsedEventData = JSON.parse(event.data);
        } catch (error) {
          console.error('Failed to parse WebSocket message:', error, event.data);
          Sentry.captureException(error, {
            extra: { message: 'Failed to parse WebSocket message', rawData: event.data },
          });
          return;
        }

        const eventData = parseWebSocketResponse(parsedEventData);
        if (!eventData) {
          console.error('Invalid websocket message:', parsedEventData);
          Sentry.captureException(new Error('Invalid websocket message'), {
            extra: { rawData: parsedEventData },
          });
          return;
        }

        // Log and ignore any traffic that is not a valid websocket message
        if (!isWebSocketError(eventData) && isUnknownWebSocketMessage(eventData)) {
          console.error('Unknown websocket message:', eventData);
          Sentry.captureException(new Error('Unknown websocket message'), {
            tags: eventData,
          });
          return;
        }

        if (eventData.type === 'complete' || eventData.type === 'error') {
          Sentry.addBreadcrumb({
            type: 'default',
            category: 'Chat Response',
            message: eventData.type === 'error' ? 'AI error received' : 'AI message received',
            data: eventData,
            level: eventData.type === 'error' ? 'error' : 'info',
          });
        }

        // These are known errors to MCP Server, so we can capture, log, and display them
        if (eventData.type === 'error') {
          Sentry.captureException(new Error(eventData.payload.code), {
            tags: {
              'mcp:code': eventData.payload.code,
              'mcp:message': eventData.payload.content,
              'mcp:sessionId': eventData.payload.sessionId,
            },
          });
          onErrorReceived?.(eventData);
        } else if (eventData.type === 'app') {
          try {
            const result = await fetchAppBundleHtml({
              resourceUri: eventData.payload.resourceUri,
              token: session.getTokens()?.session_jwt,
              mcpServerWsUrl: websocketUrl,
            });

            if (result.status !== 'success') {
              const fallbackStage =
                result.status === 'auth_error' ? 'bundle_auth_error' : 'bundle_fetch_error';
              reportMcpChartFailure({
                stage: result.status === 'not_found' ? 'bundle_not_found' : fallbackStage,
                resourceUri: eventData.payload.resourceUri,
                toolName: eventData.payload.toolName,
              });
            } else if (result.html.trim().length === 0) {
              reportMcpChartFailure({
                stage: 'bundle_empty',
                resourceUri: eventData.payload.resourceUri,
                toolName: eventData.payload.toolName,
              });
            }
          } catch (error) {
            reportMcpChartFailure({
              stage: 'bundle_fetch_error',
              resourceUri: eventData.payload.resourceUri,
              toolName: eventData.payload.toolName,
              error,
            });
          }

          if (epoch === messageEpochRef.current) {
            onMessageReceived?.(eventData);
          }
        } else {
          onMessageReceived?.(eventData);
        }

        if (eventData.type === 'complete' || eventData.type === 'error') {
          // end the span if the full message has been fully streamed or an error occurred
          finishSpan();
        }
      };
      messageQueueRef.current = messageQueueRef.current.then(processMessage, processMessage);
    },
  });

  const handleRequestTimeout = () => {
    if (resettingRef.current) {
      return;
    }
    cancelRequest();
    activeSocketRef.current = null;
    resettingRef.current = true;
    setIsResetting(true);
    socket.getWebSocket()?.close();
  };

  const handleSendMessage = (payload: ClientMessagePayload) => {
    if (resettingRef.current || socket.readyState !== ReadyState.OPEN) {
      return false;
    }
    const activeSocket = socket.getWebSocket();
    if (
      !activeSocket ||
      activeSocket !== activeSocketRef.current ||
      !('send' in activeSocket) ||
      activeSocket.readyState !== ReadyState.OPEN
    ) {
      return false;
    }

    return Sentry.startSpanManual({ name: 'AI websocket message' }, (span, finishSpan) => {
      Sentry.addBreadcrumb({
        type: 'user',
        category: 'AI Chat Request',
        message: payload.content,
        data: payload,
        level: 'info',
      });

      const message: ClientMessageType = {
        type: 'message',
        token: session.getTokens()?.session_jwt,
        instanceId: currentUser?.currentOrg.instanceId,
        payload,
        sentryMetadata: {
          sentryTrace: Sentry.spanToTraceHeader(span),
          baggage: Sentry.spanToBaggageHeader(span),
        },
      };

      try {
        activeSocket.send(JSON.stringify(message));
      } catch {
        finishSpan();
        return false;
      }
      if (activeSocket.readyState !== ReadyState.OPEN) {
        finishSpan();
        return false;
      }

      // Conclude the span when we receive the response
      stopSpanRef.current = finishSpan;
      return true;
    });
  };

  const connectionStatus = isResetting
    ? 'Connecting'
    : {
        [ReadyState.CONNECTING]: 'Connecting',
        [ReadyState.OPEN]: 'Open',
        [ReadyState.CLOSING]: 'Closing',
        [ReadyState.CLOSED]: 'Closed',
        [ReadyState.UNINSTANTIATED]: 'Uninstantiated',
      }[socket?.readyState ?? ReadyState.UNINSTANTIATED];

  return {
    onSendMessage: handleSendMessage,
    onRequestTimeout: handleRequestTimeout,
    connectionStatus,
  };
};

export default useAuthWebSocket;
